import type { SearchParameters } from '../searchParameters';
import type { SearchProfile } from '../searchProfile';
import type {
  RawAppliedFilter,
  AppliedFilterSiteExclusions,
} from './appliedFilter';
import {
  LicenseSearchFilterEnum,
  OfflineContentSearchFilterEnum,
  PhraseProcessingSearchFilterEnum,
  RecencyEnum,
  familyIncludesCreative,
} from '../searchParameters';

// Parameters that never surface as a removable chip. Either they are internal
// search plumbing (pagination, reverse-image hashes, correction suppression, …)
// or they are already surfaced by a dedicated control (the asset-type toggle,
// the family control, the sort dropdown, the search box, …) so a chip would be
// redundant. This set also covers the always-defaulted parameters (family,
// assettype, sort, page) so their default values never surface.
const STRUCTURALLY_EXCLUDED_PARAMS: ReadonlySet<string> = new Set([
  // internal / plumbing
  'agreements',
  'assetid',
  'assetfiletype',
  'clarifications',
  'contributor',
  'curatedset',
  'easyaccess',
  'editorialsubscription',
  'embeddable',
  'imagesearchhash',
  'imageurl',
  'morelikethis',
  'page',
  'premiumaccess',
  'royaltyfreesubscription',
  'recentlyviewed',
  'searchbyimage',
  'specialcollectionid',
  'stackid',
  'suppressfamilycorrection',
  'suppressphrasecorrection',
  // surfaced by a dedicated control
  'assettype',
  'family',
  'sort',
  'groupbyevent',
  'editorialproducts',
  'phrase',
  'begindate',
  'enddate',
  'mediatype',
  'excludenudity',
  'subPreview',
  'subPreviewSku',
]);

// Parameters that appear as a chip except when set to a listed no-op value (a
// default or "off" value that does not narrow the search).
const EXCLUDED_VALUES_BY_PARAM: Readonly<Record<string, readonly string[]>> = {
  recency: [RecencyEnum.ANY_DATE],
  offlinecontent: [OfflineContentSearchFilterEnum.EXCLUDE],
  // The combinations are the per-asset-type "show all" license defaults
  // (image → rf,rm; film → rf,rr), so they never narrow the search and never
  // surface as a chip. Every standalone license value is a real filter.
  license: [
    LicenseSearchFilterEnum.ROYALITY_FREE_AND_RIGHTS_MANAGED,
    LicenseSearchFilterEnum.ROYALITY_FREE_AND_RIGHTS_READY,
  ],
};

// Parameters whose chip visibility depends on the search or profile itself.
// Returns `null` when `filter` is not one of them, so the caller falls through
// to the site-level rules.
const isConditionallyExcluded = (
  filter: RawAppliedFilter,
  searchParameters: SearchParameters,
  searchProfile: SearchProfile
): boolean | null => {
  const { name, value } = filter;

  // Exact keyword-match is the only removable phrase-processing chip, and only
  // on a creative-inclusive search; otherwise no phrase-processing chip shows.
  if (name === 'phraseprocessing') {
    return familyIncludesCreative(searchParameters)
      ? value === PhraseProcessingSearchFilterEnum.DEFAULT
      : true;
  }

  // The `istockcollection` chip is removable only for subscription-repackaging
  // subscribers who are not on the essential (basic) tier — the same rule that
  // governs the horizontal collection pill.
  if (name === 'istockcollection') {
    return !searchProfile.featureConfig.collection.showCollectionPill;
  }

  return null;
};

// Decides whether a set filter's chip should be suppressed. This is orthogonal
// to `AvailableParameters#has`: availability decides whether a filter is
// offered at all, whereas this decides, for a filter that is offered and set,
// whether its applied-filter chip is shown.
export const isAppliedFilterExcluded = (
  filter: RawAppliedFilter,
  searchParameters: SearchParameters,
  searchProfile: SearchProfile,
  siteExclusions: AppliedFilterSiteExclusions
): boolean => {
  const { name, value } = filter;

  if (STRUCTURALLY_EXCLUDED_PARAMS.has(name)) return true;
  if (EXCLUDED_VALUES_BY_PARAM[name]?.includes(value)) return true;

  const conditional = isConditionallyExcluded(
    filter,
    searchParameters,
    searchProfile
  );
  if (conditional !== null) return conditional;

  if (siteExclusions.excludedParams.includes(name)) return true;
  if (siteExclusions.excludedValues[name]?.includes(value)) return true;

  return false;
};
